import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';

// Modèles
import { Client } from 'src/app/@shared/models/client/client.model';
import { Country } from 'src/app/@shared/models/core/country.model';
import { LegalStatus } from 'src/app/@shared/models/taxation/legal-status.model';
import { OrganizationActivity } from 'src/app/@shared/models/legal-entity/organization-activity.model';
import { OrganizationSize } from 'src/app/@shared/models/legal-entity/organization-size.model';
import { Timezone } from 'src/app/@shared/models/core/timezone.model';

// Utils
import { Filter } from 'src/app/@shared/utilities/models/filter';
import { Sorter } from 'src/app/@shared/utilities/models/sorter';

@Component({
    selector: 'app-client-form',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        SelectModule,
        FileUploadModule,
        ImageModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        PopoverModule,
        TextareaModule
    ],
    templateUrl: './client-form.component.html',
    styleUrls: ['./client-form.component.scss']
})
export class ClientFormComponent extends FormBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/



    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _client!: Client;
    public get client(): Client {
        return this._client;
    }
    public set client(client: Client) {
        this._client = client;
        if (!!client) {
            this._mapToForm();
            this._loadLegalStatuses(client.country);
            this._loadTimezones();
        } else {
            this.clearForm();
        }
    }

    private _legalStatuses: LegalStatus[] = [];
    public get legalStatuses(): LegalStatus[] {
        return this._legalStatuses;
    }
    public set legalStatuses(legalStatuses: LegalStatus[]) {
        let emptyArray: LegalStatus[] = [];
        this._legalStatuses = emptyArray.concat(legalStatuses);
    }

    private _mainActivities: OrganizationActivity[] = [];
    public get mainActivities(): OrganizationActivity[] {
        return this._mainActivities;
    }
    public set mainActivities(mainActivities: OrganizationActivity[]) {
        let emptyArray: OrganizationActivity[] = [];
        this._mainActivities = emptyArray.concat(mainActivities);
    }

    private _sizes: OrganizationSize[] = [];
    public get sizes(): OrganizationSize[] {
        return this._sizes;
    }
    public set sizes(sizes: OrganizationSize[]) {
        let emptyArray: OrganizationSize[] = [];
        this._sizes = emptyArray.concat(sizes);
    }

    private _timezones: Timezone[] = [];
    public get timezones(): Timezone[] {
        return this._timezones;
    }
    public set timezones(timezones: Timezone[]) {
        let emptyArray: Timezone[] = [];
        this._timezones = emptyArray.concat(timezones);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /** Une image déposée devient le logo : même droit que le bouton (#1218) */
    public override onDrop(event: DragEvent) {
        if (!this.appService.user?.hasAuthorization(this.Authorizations.ClientUpdate)) {
            event.preventDefault();
            this.dragover = false;
            return;
        }
        super.onDrop(event);
    }

    public onUploadAvatar(event: any): void {
        const files = event.files;
        let count = files.length;
        this.uploadFiles(files, 1, count, this._uploadAvatarFile$.bind(this));
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override buildForm(): void {
        this.form = this.formBuilder.group({
            businessContext: '',
            commercialName: ['', Validators.required],
            legalStatus: [null, Validators.required],
            mainActivity: [null, Validators.required],
            name: ['', Validators.required],
            shareCapital: [0, [Validators.required]],
            timezone: [null, Validators.required],
            size: null,
        })
    }

    protected override loadData(): void {
        this._loadMainActivities();
        this._loadSizes();
    }

    protected override save() {
        this._mapFromForm();
        this.clientService.save(this.client);
        this.form.markAsPristine();
    }

    protected override subscribeToObservables(): void {
        this.subscriptions.add(
            this.clientService.selected$.subscribe((client: Client) => this.client = client)
        );
    }

    protected override uploadFile$(file: File): Observable<any> {
        return this.clientService.uploadLogo$(this.client, file).pipe(
            map(() => this.clientService.get(this.client.id))
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _loadMainActivities(): void {
        this.organizationActivityService.getList$().subscribe((mainActivities: OrganizationActivity[]) => {
            this.mainActivities = mainActivities;
        });
    }

    private _loadLegalStatuses(country: Country): void {
        let filter: Filter = new Filter({
            property: 'country',
            value: country.alpha2,
        });
        let sorter: Sorter = new Sorter({
            property: 'label',
        });

        this.legalStatusService.getList$({
            filters: [filter],
            sorters: [sorter],
        }).subscribe(legalStatuses => {
            this.legalStatuses = legalStatuses;
        });
    }

    private _loadSizes(): void {
        let sorter: Sorter = new Sorter({
            property: 'code',
        });
        this.organizationSizeService.getList$({
            sorters: [sorter],
        }).subscribe((sizes: OrganizationSize[]) => {
            this.sizes = sizes;
        });
    }

    private _loadTimezones(): void {
        let filter: Filter = new Filter({
            property: 'countries',
            value: this.client?.country?.alpha2,
        });
        let sorter: Sorter = new Sorter({
            property: 'label',
        });

        this.timezoneService.getList$({
            filters: [filter],
            sorters: [sorter],
        }).subscribe((timezones: Timezone[]) => {
            this.timezones = timezones;
        });
    }

    private _mapFromForm(): void {
        this.client.businessContext = this.form.get('businessContext')!.value;
        this.client.commercialName = this.form.get('commercialName')!.value;
        this.client.taxationSettings.legalStatus = this.form.get('legalStatus')!.value;
        this.client.mainActivity = this.form.get('mainActivity')!.value;
        this.client.name = this.form.get('name')!.value;
        this.client.shareCapital = this.form.get('shareCapital')!.value;
        this.client.timezone = this.form.get('timezone')!.value;
        this.client.size = this.form.get('size')?.value;
    }

    private _mapToForm(): void {
        this.form.setValue({
            businessContext: !!this.client.businessContext ? this.client.businessContext : '',
            commercialName: !!this.client.commercialName ? this.client.commercialName : '',
            legalStatus: !!this.client.taxationSettings.legalStatus ? this.client.taxationSettings.legalStatus : null,
            mainActivity: !!this.client.mainActivity ? this.client.mainActivity : null,
            name: !!this.client.name ? this.client.name : '',
            shareCapital: !!this.client.shareCapital ? this.client.shareCapital : 0,
            timezone: !!this.client.timezone ? this.client.timezone : null,
            size: !!this.client.size ? this.client.size : null,
        });
    }

    private _uploadAvatarFile$(file: File): Observable<any> {
        return this.clientService.uploadAvatar$(this.client, file).pipe(
            map(() => this.clientService.get(this.client.id))
        );
    }

}
